import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePageDetailQuery } from "../../api/pages/api";
import { useConsultantListQuery } from "../../api/consultants/api";
import { ConsultantListParams } from "../../types";
import HeaderImage from "../../components/HeaderImage";
import Heading from "../../components/Heading";
import Loader from "../../components/Loader";
import ConsultantFilters from "./components/ConsultantFilters";
import ConsultantMap from "./components/ConsultantMap";
import FAQSection from "./components/FAQSection";
import { REGION_CENTERS, DEFAULT_CENTER, DEFAULT_ZOOM } from "./regionCenters";

const HelpPage = () => {
    const { t } = useTranslation();
    const { data: page } = usePageDetailQuery({ slug: "help" });
    const [filters, setFilters] = useState<ConsultantListParams>({});
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);

    const { data: consultants, isFetching: isLoading } =
        useConsultantListQuery(filters);

    const handleRegionChange = (
        regionId: number | undefined,
        regionName: string | undefined,
    ) => {
        setFilters((prev) => ({ ...prev, region_id: regionId }));

        if (regionName && REGION_CENTERS[regionName]) {
            const center = REGION_CENTERS[regionName];
            setMapCenter({ lat: center.lat, lng: center.lng });
            setMapZoom(center.zoom);
        } else {
            setMapCenter(DEFAULT_CENTER);
            setMapZoom(DEFAULT_ZOOM);
        }
    };

    const handleSearchChange = (search: string) => {
        if (!search.trim()) {
            setFilters((prev) => ({
                ...prev,
                city: undefined,
                zip: undefined,
            }));
            setMapCenter(DEFAULT_CENTER);
            setMapZoom(DEFAULT_ZOOM);
            return;
        }

        // Detect if the search is a ZIP code (starts with digits)
        const isZip = /^\d/.test(search.trim());

        if (isZip) {
            setFilters((prev) => ({
                ...prev,
                zip: search.trim(),
                city: undefined,
            }));
        } else {
            setFilters((prev) => ({
                ...prev,
                city: search.trim(),
                zip: undefined,
            }));
        }

        // Zoom into matching consultants if available
        if (consultants && consultants.length > 0) {
            const matching = consultants.filter((c) => {
                if (isZip) return c.zip.startsWith(search.trim());
                return c.city
                    .toLowerCase()
                    .includes(search.trim().toLowerCase());
            });
            if (matching.length > 0) {
                const avgLat =
                    matching.reduce((sum, c) => sum + c.latitude, 0) /
                    matching.length;
                const avgLng =
                    matching.reduce((sum, c) => sum + c.longitude, 0) /
                    matching.length;
                setMapCenter({ lat: avgLat, lng: avgLng });
                setMapZoom(matching.length === 1 ? 13 : 10);
            }
        }
    };

    // Zoom to matching results after data loads
    const displayConsultants = useMemo(() => consultants || [], [consultants]);

    return (
        <div className="content-bg">
            <HeaderImage imageUrl={page?.image || "/pages/01_o-nas.jpg"} />
            <div className="container mx-auto">
                <div className="bg-white p-8 md:py-16">
                    <Heading>
                        {page?.title || t("consultantFinder.title")}
                    </Heading>

                    {page?.content && (
                        <div
                            className="content mb-6"
                            dangerouslySetInnerHTML={{ __html: page.content }}
                        />
                    )}

                    <div className="flex justify-center mb-8">
                        <img
                            src="/ruka-3_svetla.svg"
                            alt=""
                            className="w-[350px]"
                        />
                    </div>

                    <ConsultantFilters
                        onRegionChange={handleRegionChange}
                        onSearchChange={handleSearchChange}
                    />

                    {isLoading ? (
                        <div className="relative h-[500px]">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            {displayConsultants.length === 0 ? (
                                <div className="text-center py-12 text-gray-500 border border-gray-300 rounded-lg">
                                    {t("consultantFinder.noResults")}
                                </div>
                            ) : (
                                <ConsultantMap
                                    consultants={displayConsultants}
                                    center={mapCenter}
                                    zoom={mapZoom}
                                />
                            )}
                        </>
                    )}

                    <FAQSection />

                    <p className="text-xs text-gray-500 mt-8">
                        <strong>Upozornenie:</strong> Komunita Bafpro vytvára
                        priestor na prepájanie certifikovaných finančných
                        poradcov a záujemcov o ich služby. Bafpro nenesie
                        zodpovednosť za rady, odporúčania ani rozhodnutia, ktoré
                        jednotliví poradcovia poskytujú. Každý poradca koná
                        samostatne podľa svojho odborného úsudku a etických
                        zásad.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default HelpPage;
